import {
    observer
} from 'mobx-react-lite';
import type { ApplicationSdk } from '../../stores';
import { useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { InputSwitch } from 'primereact/inputswitch';
import ObjectManagerStore from '../../stores/manager/ObjectManagerStore';
import { formatDate } from '../../helper';
import ObjectDialogStore from '../../stores/manager/dialog/ObjectDialogStore';
import SearchField from '../../components/SearchField';
import { Paginator } from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Chip } from 'primereact/chip';

const ObjectManager = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return <>
        <Button
            label="Neu"
            icon="pi pi-plus"
            aria-label="Filter"
            onClick={() => sdk.openDialog(ObjectDialogStore, {})}
            style={{ marginRight: '0.5rem' }}
        />
    </>
});

const FilterChips = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const selectedManager = sdk.currentStore?.filterManager;
    const archived = sdk.currentStore?.includeArchived;
    const selectedSearch = sdk.currentStore?.searchValue || '';
    const clearManagerFilter = () => {
        sdk.currentStore.applyFilterManager(null); // Clear the manager filter
    };
    const clearArchivedFilter = () => {
        sdk.currentStore.setIncludeArchived(false); // Clear the manager filter
    };

    return (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {selectedManager && (
                <Chip
                    label={`Objektleiter: ${selectedManager.name}`}
                    removable
                    onRemove={clearManagerFilter}
                />
            )}
            {archived && (
                <Chip
                    label={`Archivierte Objekte`}
                    removable
                    onRemove={clearArchivedFilter}
                />
            )}
            {selectedSearch && (
                <Chip
                    label={`Suche: ${selectedSearch}`}
                    removable
                    onRemove={() => sdk.currentStore.clearSearch()}
                />
            )}
        </div>
    );
});

const FilterPopover = observer(({ sdk }: { sdk: ApplicationSdk }) => {

    // const [selectedManager, setSelectedManager] = useState(null);

    const loadManagers = async () => {
        if (sdk.currentStore) {
            sdk.currentStore.fetchAvailableManagers();
        }
    };

    const onManagerChange = (e: any) => {
        sdk.currentStore.applyFilterManager(e.value);
    };

    return (
        <div>
            <h4 style={{ marginBottom: '1rem' }}>Objektleiter auswählen</h4>
            <Dropdown
                value={sdk.currentStore?.filterManager || null}
                options={sdk.currentStore?.available_managers || []}
                onChange={onManagerChange}
                onShow={loadManagers} // Lazy load managers when dropdown is focused
                placeholder="Objektleiter auswählen"
                optionLabel="name"
                loading={false}
                style={{ width: '100%' }}
            />
            <h4 style={{ marginBottom: '1rem' }}>Sonstige Filter</h4>
            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center' }}>
                <label htmlFor="showArchived" style={{ marginRight: '0.5rem' }}>Archivierte Objekte</label>
                <InputSwitch
                    id="showArchived"
                    checked={sdk.currentStore?.includeArchived || false}
                    onChange={(e) => {
                        sdk.currentStore.setIncludeArchived(e.value);
                    }}
                />
            </div>
        </div>
    );
});

const SearchToolbar = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const ref: any = useRef(null);

    return <>
        <Toolbar start={() => <ObjectManager sdk={sdk} />}
            center={() => (
                <>
                    <Button
                        icon="pi pi-filter"
                        className="p-button-text"
                        label="Filter"
                        onClick={(e: any) => ref.current?.toggle(e)}
                        aria-label="Filter"
                    />
                    <OverlayPanel ref={ref} style={{ width: '300px' }}>
                        <FilterPopover sdk={sdk} />
                    </OverlayPanel>
                </>
            )}
            end={() => <SearchField store={sdk.currentStore} placeholder={'Objekte durchsuchen'} />} />
        <FilterChips sdk={sdk} />
    </>
});

const ObjectOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(ObjectManagerStore);
    }, [sdk]);

    const objects = sdk.currentStore?.allObjects || null;

    const onPageChange = (event: any) => {
        sdk.currentStore.onPageChange(event.page + 1);
    };


    return <div className="card">
        <DataTable
            value={objects}
            emptyMessage={'Keine Objekte gefunden'}
            header={<SearchToolbar sdk={sdk} />}
            onSort={(event: any) => {
                sdk.currentStore.onSort(event.sortField);
                return event;
            }}
            sortOrder={sdk.currentStore?.sortOrder}
            sortField={sdk.currentStore?.sortField || 'id'}
            rowHover={true}
            loading={!!sdk.currentStore?.objectListLoading}
        >
            <Column field="object_number" header="Objekt-Id" sortable ></Column>
            <Column field="name" header="Name" sortable></Column>
            <Column field="address" header="Adresse"></Column>
            <Column field="manager.name" header="Objektleiter"></Column>
            <Column field={'created'} header="Erstellt" sortable body={(p: any) => formatDate(p.created)}></Column>
            <Column field={'updated'} header="Aktualisiert" sortable body={(p: any) => formatDate(p.updated)}></Column>
            <Column header="" body={(o: any) => <>
                <Button icon="pi pi-pencil" rounded text aria-label="Filter" onClick={() => sdk.openDialog(ObjectDialogStore, { object_id: o.id })} />
                {sdk.currentStore.includeArchived && (
                    <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger" // Weniger markant durch "p-button-text"
                        style={{ width: '2.5rem', height: '2.5rem' }} // Kleinere Größe
                        onClick={() => {
                            sdk.openDeleteConfirmationDialog(
                                'Objekt löschen',
                                `Möchten Sie das Objekt '${o.name}' wirklich löschen?`,
                                async (result: boolean) => {
                                    if (result) {
                                        try {
                                            await sdk.currentStore.deleteObject(o.id);
                                            sdk.showSuccessToast('Objekt gelöscht');

                                        } catch (error: any) {
                                            sdk.showErrorToast(error?.message || 'Unbekannter Fehler')
                                        }
                                    }
                                },
                                'löschen'
                            );
                        }}
                    />
                )}
            </>}></Column>
        </DataTable>
        <Paginator
            first={((sdk.currentStore?.currentPage || 1) - 1) * 30}
            rows={30}
            totalRecords={sdk.currentStore?.totalCount || 1}
            onPageChange={onPageChange}
            // rowsPerPageOptions={30}
            template="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate={`Seite {currentPage} / {totalPages} (${sdk.currentStore?.totalCount || 0} Objekten)`}
        />
    </div>;
});

export default ObjectOverview;